import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Package, Printer, ScanLine, Undo2 } from 'lucide-react';
import {
  MAX_RECEIPT_LINES,
  type NomenclatureDto,
  type ReceiptWithLinesDto,
  type Unit,
} from '@eombor/contracts';
import { Badge, Button, Card, Dialog, EmptyState, Input, cn, useToast } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { useScannerClaim } from '../../../shared/scanner/scanner-claim.js';
import { Select } from '../../../shared/ui/Select.js';
import { usePermission } from '../../auth/model/usePermission.js';
import { NomenclaturePicker } from '../../catalog/ui/NomenclaturePicker.js';
import { useWarehouses } from '../../warehouse/model/warehouse.queries.js';
import { LabelPrintDialog } from '../../warehouse/ui/LabelPrintDialog.js';
import { BatchLabelDialog, type BatchLabelTarget } from './BatchLabelDialog.js';
import { stockApi, type CreateReceiptBody } from '../api/stock.api.js';
import { useCellStock } from '../model/cell-stock.js';
import { stockKeys } from '../model/stock.queries.js';
import {
  emptyLine,
  tallyLines,
  tallyText,
  toLineInputs,
  type ReceiptLineDraft,
} from '../model/receipt-line.js';
import { isRetryableConflict, stockErrorText } from '../model/stock-error.js';
import { RECEIPT_ROW_GRID, ReceiptLineRow } from './ReceiptLineRow.js';
import { unitText } from '../../../shared/i18n/labels.js';

const RECEIVE_PERMISSION = 'stock.receive';

/** Bugungi sana `<input type="date">` formatida (mahalliy vaqt bo'yicha). */
const today = (): string => {
  const now = new Date();
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

let lineSeq = 0;
const nextLineKey = (): string => {
  lineSeq += 1;
  return `line-${lineSeq}`;
};

/**
 * Idempotentlik kaliti (`idempotencyKeySchema`: kamida 8 belgi, `A-Za-z0-9._:-`).
 *
 * `randomUUID` yo'q muhitlar ham bor (HTTPS bo'lmagan kontekst, eski jsdom) —
 * u yerda vaqt va tasodifdan yasaladi. Kalitning maqsadi bitta: BIR urinishning
 * takroriy yuborilishini server tanib olsin; global unikallik shart emas.
 */
const newIdempotencyKey = (): string =>
  typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

export interface ReceiptPageProps {
  /** Ombor sahifasidan kelganda oldindan tanlangan ombor. */
  initialWarehouseId?: string;
  /** Qoldiq bo'limiga qaytish — sahifa marshrutlashni bilmaydi. */
  onDone?: () => void;
  /** Sarlavha yonidagi "Orqaga qaytish" — berilmasa tugma chizilmaydi. */
  onBack?: () => void;
}

/**
 * Kirim ekrani — SKANER BIRINCHI.
 *
 * Skladchi javon oldida qo'lida skaner bilan turadi: fokus doim katak kodida
 * boshlanadi, kod tasdiqlangach o'zi miqdorga o'tadi, miqdordan keyingi
 * `Enter` esa yangi satr ochib fokusni yana katak kodiga qaytaradi. Butun
 * hujjat sichqonchaga tegmasdan to'ldiriladi.
 *
 * Saqlash ikki qadam: oldindan ko'rish (nima yoziladi) va tasdiq. Har
 * urinishda BITTA `Idempotency-Key` yuboriladi — tarmoq uzilib qayta
 * yuborilganda hujjat ikkilanmaydi.
 */
export const ReceiptPage = ({ initialWarehouseId = '', onDone, onBack }: ReceiptPageProps) => {
  /* Skanerlangan katak kodi SATRGA tushishi kerak, boshqa sahifani ochishi
     emas — hujjat yarim to'ldirilgan holda yo'qolib qolardi. */
  useScannerClaim();

  const { can, canAnywhere } = usePermission();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [warehouseId, setWarehouseId] = useState(initialWarehouseId);
  const [supplier, setSupplier] = useState('');
  const [receivedAt, setReceivedAt] = useState(today);
  const [defaultNomenclature, setDefaultNomenclature] = useState<NomenclatureDto | null>(null);
  const [lines, setLines] = useState<ReceiptLineDraft[]>(() => [emptyLine(nextLineKey())]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saved, setSaved] = useState<ReceiptWithLinesDto | null>(null);
  const [labelOpen, setLabelOpen] = useState(false);
  const [batchLabelOpen, setBatchLabelOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const warehouses = useWarehouses({ limit: 200 });

  /**
   * Ombordagi qoldiqning katak kesimi — satr ostidagi "Hozir: … → kirimdan
   * keyin: …" izohi shundan chiziladi. Bitta so'rov butun sahifaga yetadi.
   */
  const cellStock = useCellStock(warehouseId);

  /**
   * TANLANGAN nomenklaturalar yodda qoladi: satrda faqat `nomenclatureId`
   * saqlanadi (hujjatga o'sha yoziladi), ekranga esa nom va BIRLIK kerak —
   * birliksiz miqdor tekshiruvi ma'nosini yo'qotadi.
   */
  const [known, setKnown] = useState<Map<string, NomenclatureDto>>(new Map());
  const remember = useCallback((item: NomenclatureDto | null): void => {
    if (item === null) return;
    setKnown((prev) => (prev.has(item.id) ? prev : new Map(prev).set(item.id, item)));
  }, []);

  const unitOf = useCallback(
    (nomenclatureId: string): Unit | null => known.get(nomenclatureId)?.unit ?? null,
    [known],
  );

  const nomenclatureOf = (id: string | null): NomenclatureDto | null =>
    id === null ? null : (known.get(id) ?? null);

  /* Har satrga BARQAROR ref: ro'yxat qayta chizilganda ham fokus egasi
     o'zgarmaydi (`useRef` ichidagi xarita kalit bo'yicha bir marta yasaydi). */
  const refsRef = useRef(new Map<string, RefObject<HTMLInputElement | null>>());
  const inputRef = (key: string): RefObject<HTMLInputElement | null> => {
    const existing = refsRef.current.get(key);
    if (existing !== undefined) return existing;
    const created: RefObject<HTMLInputElement | null> = { current: null };
    refsRef.current.set(key, created);
    return created;
  };

  /* Ombor ro'yxati RUXSAT bo'yicha filtrlanadi: kirim qila olmaydigan omborni
     tanlab, xatoni faqat saqlashda ko'rish — bekorga sarflangan mehnat.
     `useMemo` YO'Q: `can` har chizishda yangi funksiya bo'ladi va bog'liqlik
     ro'yxati yolg'on bo'lib qolardi; filtr esa 200 satrga arzon. */
  const receivableWarehouses = (warehouses.data?.items ?? []).filter((warehouse) =>
    can(RECEIVE_PERMISSION, {
      warehouseId: warehouse.id,
      mtuId: warehouse.mtuId,
      orgUnitId: warehouse.orgUnitId,
    }),
  );

  const selectedWarehouse = receivableWarehouses.find((warehouse) => warehouse.id === warehouseId);
  const canPrint =
    selectedWarehouse !== undefined &&
    can('warehouse.label.print', {
      warehouseId: selectedWarehouse.id,
      mtuId: selectedWarehouse.mtuId,
      orgUnitId: selectedWarehouse.orgUnitId,
    });

  /* Bekor qilish AYNAN shu ombor doirasida so'raladi: `stock.adjust` ni
     boshqa omborda olgan skladchi bu hujjatga tegmasligi kerak. */
  const canCancel =
    selectedWarehouse !== undefined &&
    can('stock.adjust', {
      warehouseId: selectedWarehouse.id,
      mtuId: selectedWarehouse.mtuId,
      orgUnitId: selectedWarehouse.orgUnitId,
    });

  // Fokus birinchi katak kodida boshlanadi — sahifa ochilishi bilan skaner ishlaydi.
  const firstKey = lines[0]?.key;
  useEffect(() => {
    if (firstKey === undefined) return;
    refsRef.current.get(`${firstKey}:cell`)?.current?.focus();
  }, [firstKey]);

  const patchLine = (key: string, patch: Partial<ReceiptLineDraft>): void => {
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };

  const appendLine = (): void => {
    const key = nextLineKey();
    setLines((prev) => [...prev, emptyLine(key, defaultNomenclature?.id ?? null)]);
    // Yangi satr chizilgandan keyin fokus uning katak kodiga qaytadi.
    requestAnimationFrame(() => refsRef.current.get(`${key}:cell`)?.current?.focus());
  };

  const removeLine = (key: string): void => {
    setLines((prev) => {
      if (prev.length === 1) return [emptyLine(nextLineKey(), prev[0]?.nomenclatureId ?? null)];
      const index = prev.findIndex((line) => line.key === key);
      const next = prev.filter((line) => line.key !== key);
      const neighbour = next[Math.max(0, index - 1)];
      if (neighbour !== undefined) {
        requestAnimationFrame(() =>
          refsRef.current.get(`${neighbour.key}:cell`)?.current?.focus(),
        );
      }
      return next;
    });
  };

  const tally = useMemo(() => tallyLines(lines, unitOf), [lines, unitOf]);
  const lineInputs = useMemo(() => toLineInputs(lines, unitOf), [lines, unitOf]);

  const idempotencyKeyRef = useRef<string | null>(null);
  const body = (): CreateReceiptBody => ({
    warehouseId,
    ...(supplier.trim() === '' ? {} : { supplier: supplier.trim() }),
    receivedAt: new Date(`${receivedAt}T00:00:00`).toISOString(),
    lines: lineInputs,
  });

  const save = useMutation({
    mutationFn: () => {
      // Kalit URINISH uchun bir marta yasaladi: qayta yuborishda AYNAN
      // shu kalit ketadi va server takroriy hujjat ochmaydi.
      idempotencyKeyRef.current ??= newIdempotencyKey();
      return stockApi.createReceipt(body(), idempotencyKeyRef.current);
    },
    onSuccess: (receipt) => {
      idempotencyKeyRef.current = null;
      /* Qoldiq o'zgardi — katak kesimi ham eskirdi. "Yangi kirim" bosilganda
         satr ostida ESKI "Hozir: …" turib qolmasligi kerak. */
      void queryClient.invalidateQueries({ queryKey: stockKeys.all });
      setPreviewOpen(false);
      setSaved(receipt);
      toast.show(t('stock.receiptSaved', { docNo: receipt.docNo }), 'success');
    },
  });

  /**
   * XATO KIRIMNI BEKOR QILISH — saqlangandan keyingi oynada.
   *
   * Hujjat o'chmaydi: backend teskari `ADJUST` yozuvini qo'shadi va kirimni
   * "bekor qilingan" deb belgilaydi (`receipt.service.ts` dagi `cancel`).
   * Tugma AYNAN shu yerda turadi, chunki xato eng ko'p saqlangandan keyingi
   * daqiqalarda payqaladi — o'shanda hujjat hali ko'z oldida.
   */
  const cancel = useMutation({
    mutationFn: (input: { id: string; reason: string }) =>
      stockApi.cancelReceipt(input.id, input.reason),
    onSuccess: (receipt) => {
      // Qoldiq kataklardan qaytdi — kesimlar ham eskirdi.
      void queryClient.invalidateQueries({ queryKey: stockKeys.all });
      setCancelOpen(false);
      setCancelReason('');
      setSaved(null);
      toast.show(t('stock.receiptCancelled', { docNo: receipt.docNo }), 'success');
    },
  });

  const savedCellIds = useMemo(
    () => (saved === null ? [] : [...new Set(saved.lines.map((line) => line.cellId))]),
    [saved],
  );

  /**
   * Bosiladigan PARTIYALAR — kirim satrlarining o'zidan.
   *
   * Har satr bitta partiya tug'diradi (`batchId` javobda qaytadi), ya'ni
   * qo'shimcha so'rov kerak emas. Nomenklatura nomi `known` xaritasidan:
   * u satr terilayotganda allaqachon yuklangan.
   */
  const savedBatches = useMemo<BatchLabelTarget[]>(
    () =>
      saved === null
        ? []
        : saved.lines.map((line) => {
            const ref = known.get(line.nomenclatureId);
            const unit = ref === undefined ? '' : ` ${unitText(ref.unit)}`;
            return {
              batchId: line.batchId,
              batchNo: line.batchNo,
              name: ref?.name ?? line.batchNo,
              hint: `${String(line.qty)}${unit}`,
            };
          }),
    [saved, known],
  );

  const closeCancel = (): void => {
    setCancelOpen(false);
    setCancelReason('');
    cancel.reset();
  };

  const startNewReceipt = (): void => {
    setSaved(null);
    setLines([emptyLine(nextLineKey(), defaultNomenclature?.id ?? null)]);
    save.reset();
  };

  if (!canAnywhere(RECEIVE_PERMISSION)) {
    return (
      <Card>
        <EmptyState title={t('stock.receiveForbidden')} description={t('stock.receiveForbiddenHint')} />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {onBack === undefined ? null : (
              <>
                {/* `-ml-3` — ikonka sahifa chetiga tekislanadi, `ghost`
                    tugmaning ichki bo'shlig'i esa bosish maydonida qoladi. */}
                <Button variant="ghost" size="sm" onClick={onBack} className="-ml-3">
                  <ArrowLeft size={16} aria-hidden />
                  {t('stock.receiptBack')}
                </Button>
                <span aria-hidden className="bg-border h-5 w-px" />
              </>
            )}
            <h1 className="text-xl font-semibold tracking-tight text-text">
              {t('stock.receiptTitle')}
            </h1>
          </div>
          <p className="max-w-prose text-sm text-text-muted">{t('stock.receiptSubtitle')}</p>
        </div>

        {/* Jonli hisob o'ng yuqorida: "12 satr · 480 dona". Skladchi hujjatni
            yopishdan oldin faqat shu raqamga qaraydi. */}
        <div className="flex flex-col items-end gap-1">
          <span className="text-xs text-text-faint">{t('stock.tallyLabel')}</span>
          <span aria-live="polite" className="tabular text-lg font-semibold text-text">
            {tallyText(tally)}
          </span>
        </div>
      </header>

      <Card title={t('stock.docSectionTitle')}>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <Select
            label={t('stock.fieldWarehouse')}
            placeholder={t('stock.selectWarehouse')}
            value={warehouseId}
            onChange={(event) => setWarehouseId(event.target.value)}
            options={receivableWarehouses.map((warehouse) => ({
              value: warehouse.id,
              label: `${warehouse.code} — ${warehouse.name}`,
            }))}
            hint={receivableWarehouses.length === 0 ? t('stock.noReceivableWarehouse') : undefined}
          />

          <Input
            label={t('stock.fieldSupplier')}
            autoComplete="off"
            value={supplier}
            onChange={(event) => setSupplier(event.target.value)}
          />

          <Input
            label={t('stock.fieldReceivedAt')}
            type="date"
            value={receivedAt}
            onChange={(event) => setReceivedAt(event.target.value)}
          />
        </div>

        {/* BITTA boshqaruv: yozganda natija shu yerda ko'rinadi va tanlangani
            satrlarga tushadi. Ilgari bu yerda qidiruv maydoni bilan YONMA-YON
            yopiq `Select` turardi — yozilgani ro'yxatga tushar, ekranda esa
            hech nima o'zgarmasdi. */}
        <div className="mt-4 grid gap-4 border-t border-border pt-4 md:grid-cols-2">
          <NomenclaturePicker
            label={t('stock.searchNomenclature')}
            placeholder={t('stock.searchNomenclaturePlaceholder')}
            hint={t('stock.fieldDefaultNomenclatureHint')}
            value={defaultNomenclature}
            onSelect={(item) => {
              setDefaultNomenclature(item);
              remember(item);
              // Bo'sh satrlar darhol to'ladi: bir pallet bir xil mahsulot
              // ko'p katakka bo'linadi va har satrda qayta tanlash ortiqcha.
              setLines((prev) =>
                prev.map((line) =>
                  line.nomenclatureId === null ? { ...line, nomenclatureId: item?.id ?? null } : line,
                ),
              );
            }}
          />
        </div>
      </Card>

      <Card title={t('stock.linesSectionTitle')} padded={false}>
        <div className="flex flex-col">
          <div
            className={cn(
              RECEIPT_ROW_GRID,
              'border-b border-border bg-surface-2/60 px-5 py-2',
              'text-xs font-semibold tracking-wide text-text-muted uppercase',
            )}
          >
            <span>№</span>
            <span>{t('stock.colNomenclature')}</span>
            <span>{t('stock.colCell')}</span>
            <span>{t('stock.colQty')}</span>
            <span>{t('stock.colState')}</span>
            <span className="sr-only">{t('stock.colActions')}</span>
          </div>

          <ul className="flex flex-col gap-1 px-4 py-3">
            {lines.map((line, index) => (
              <ReceiptLineRow
                key={line.key}
                line={line}
                index={index + 1}
                warehouseId={warehouseId}
                nomenclature={nomenclatureOf(line.nomenclatureId)}
                unit={line.nomenclatureId === null ? null : unitOf(line.nomenclatureId)}
                cellStock={cellStock.data ?? null}
                cellRef={inputRef(`${line.key}:cell`)}
                qtyRef={inputRef(`${line.key}:qty`)}
                canRemove={lines.length > 1}
                onChange={(patch) => patchLine(line.key, patch)}
                onSelectNomenclature={(item) => {
                  remember(item);
                  patchLine(line.key, { nomenclatureId: item?.id ?? null });
                }}
                onRemove={() => removeLine(line.key)}
                onCommit={appendLine}
                onCellResolved={(cell) => {
                  // Katak tasdiqlandi va miqdor bo'sh — fokus o'zi miqdorga o'tadi.
                  if (cell === null || line.qty !== '') return;
                  refsRef.current.get(`${line.key}:qty`)?.current?.focus();
                }}
              />
            ))}
          </ul>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3">
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={appendLine}
                disabled={lines.length >= MAX_RECEIPT_LINES}
              >
                {t('stock.addLine')}
              </Button>
              <span className="text-xs text-text-faint">{t('stock.addLineHint')}</span>
            </div>

            <Button
              onClick={() => setPreviewOpen(true)}
              disabled={warehouseId === '' || lineInputs.length === 0}
            >
              <ScanLine size={14} aria-hidden />
              {t('stock.review')}
            </Button>
          </div>
        </div>
      </Card>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={t('stock.previewTitle')}
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setPreviewOpen(false)}>
              {t('stock.cancel')}
            </Button>
            <Button loading={save.isPending} onClick={() => save.mutate()}>
              {t(isRetryableConflict(save.error) ? 'stock.retry' : 'stock.confirmReceipt')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {save.isError ? (
            <p
              role="alert"
              className={cn(
                'rounded-[var(--radius-sm)] px-3 py-2 text-sm',
                isRetryableConflict(save.error)
                  ? 'border border-warn/25 bg-warn-soft text-warn'
                  : 'border border-danger/25 bg-danger-soft text-danger',
              )}
            >
              {stockErrorText(save.error, t('stock.saveFailed'))}
            </p>
          ) : null}

          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex flex-col gap-0.5">
              <dt className="text-xs text-text-faint">{t('stock.fieldWarehouse')}</dt>
              <dd className="text-text">
                {receivableWarehouses.find((warehouse) => warehouse.id === warehouseId)?.name ?? '—'}
              </dd>
            </div>
            <div className="flex flex-col gap-0.5">
              <dt className="text-xs text-text-faint">{t('stock.fieldSupplier')}</dt>
              <dd className="text-text">{supplier === '' ? '—' : supplier}</dd>
            </div>
            <div className="flex flex-col gap-0.5">
              <dt className="text-xs text-text-faint">{t('stock.fieldReceivedAt')}</dt>
              <dd className="tabular text-text">{receivedAt}</dd>
            </div>
          </dl>

          <p className="tabular text-sm font-semibold text-text">{tallyText(tally)}</p>

          <ul className="flex max-h-64 flex-col gap-1 overflow-y-auto">
            {lines
              .filter((line) => line.cellId !== null && line.nomenclatureId !== null)
              .map((line) => {
                const nomenclature =
                  line.nomenclatureId === null ? undefined : known.get(line.nomenclatureId);
                return (
                  <li
                    key={line.key}
                    className="flex items-center justify-between gap-3 border-b border-border pb-1 text-sm last:border-b-0"
                  >
                    <span className="min-w-0 truncate text-text">
                      {nomenclature === undefined ? line.nomenclatureId : nomenclature.name}
                    </span>
                    <span className="tabular shrink-0 text-text-muted">{line.cellCode}</span>
                    <span className="tabular shrink-0 text-text">
                      {line.qty}
                      {nomenclature === undefined ? '' : ` ${unitText(nomenclature.unit)}`}
                    </span>
                  </li>
                );
              })}
          </ul>
        </div>
      </Dialog>

      <Dialog
        open={saved !== null}
        onClose={() => setSaved(null)}
        title={t('stock.savedTitle')}
        footer={
          <>
            <Button variant="ghost" onClick={startNewReceipt}>
              {t('stock.newReceipt')}
            </Button>
            <Button
              onClick={() => {
                setSaved(null);
                if (onDone !== undefined) onDone();
              }}
            >
              {t('stock.done')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-text">
            {t('stock.savedBody', {
              docNo: saved?.docNo ?? '',
              lines: saved?.lines.length ?? 0,
            })}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="success" mono>
              {saved?.docNo ?? ''}
            </Badge>
            {canPrint ? (
              <>
                {/* MAHSULOT etiketkasi birinchi: u har kirimda kerak
                    bo'ladi, javon yorlig'i esa faqat yangi katak
                    ochilganda yoki eskisi yirtilganda. */}
                <Button variant="secondary" size="sm" onClick={() => setBatchLabelOpen(true)}>
                  <Package size={14} aria-hidden />
                  {t('stock.printBatchLabels')}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setLabelOpen(true)}>
                  <Printer size={14} aria-hidden />
                  {t('stock.printLabels')}
                </Button>
              </>
            ) : null}
            {canCancel ? (
              <Button variant="ghost" size="sm" onClick={() => setCancelOpen(true)}>
                <Undo2 size={14} aria-hidden />
                {t('stock.receiptCancel')}
              </Button>
            ) : null}
          </div>
          <p className="text-xs text-text-faint">
            {t(canPrint ? 'stock.printLabelsHint' : 'stock.printForbidden')}
          </p>
        </div>
      </Dialog>

      <Dialog
        open={cancelOpen}
        onClose={closeCancel}
        size="sm"
        title={t('stock.receiptCancelTitle')}
        /* Sabab TERILADI — ya'ni bu forma, oddiy tasdiq emas. Shuning uchun
           `ConfirmDialog` emas, `Dialog` bo'lib qoladi; lekin endi terilgan
           sabab tasodifan yopilganda yo'qolmaydi. */
        dirty={cancelReason.trim().length > 0}
        busy={cancel.isPending}
        error={
          cancel.error === null || cancel.error === undefined
            ? undefined
            : cancel.error instanceof ApiClientError
              ? cancel.error.message
              : t('stock.receiptCancelFailed')
        }
        footer={
          <>
            <Button variant="ghost" onClick={closeCancel}>
              {t('stock.cancel')}
            </Button>
            <Button
              variant="danger"
              loading={cancel.isPending}
              disabled={cancelReason.trim().length < 3}
              onClick={() => {
                if (saved !== null) cancel.mutate({ id: saved.id, reason: cancelReason.trim() });
              }}
            >
              {t('stock.receiptCancel')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Badge tone="neutral" mono>
            {saved?.docNo ?? ''}
          </Badge>

          <p className="text-text-muted text-sm text-pretty">{t('stock.receiptCancelWarning')}</p>

          <Input
            label={t('stock.receiptCancelReason')}
            hint={t('stock.receiptCancelReasonHint')}
            value={cancelReason}
            onChange={(event) => setCancelReason(event.target.value)}
          />

        </div>
      </Dialog>

      {/* Etiketka oynasi 03-rejadan QAYTA ISHLATILADI — ikkinchi nusxa yo'q. */}
      {canPrint ? (
        <>
          <LabelPrintDialog
            open={labelOpen}
            onClose={() => setLabelOpen(false)}
            cellIds={savedCellIds}
          />
          <BatchLabelDialog
            open={batchLabelOpen}
            onClose={() => setBatchLabelOpen(false)}
            targets={savedBatches}
          />
        </>
      ) : null}
    </div>
  );
};
